import { useEffect, useState, useCallback } from 'react';
import Box from 'elements/Box';
import {
  Button,
  Chip,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import AlertDialog from 'elements/AlertDialog';
import { ApiRequest } from 'GlobalFunctions';
import { useSocketEvent } from 'SocketContext';
import SafetyAlertDetails from './SafetyAlertDetails';
import SafetyAlertSender from './SafetyAlertSender';

import 'assets/css/main.css';

export default function SafetyAlertsList(props: any) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = props;

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('');

  const [openDetails, setOpenDetails] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);

  const [openSender, setOpenSender] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    loadList();
  }, [page, filterType]);

  // Real-time updates
  useSocketEvent('safety_alert_response', useCallback(() => { loadList(); }, []));
  useSocketEvent('safety_emergency', useCallback(() => { loadList(); }, []));

  const loadList = async () => {
    setLoading(true);
    const filters: any = {};
    if (filterType) filters.alert_type = filterType;

    await ApiRequest({
      url: '/safety-alerts/list',
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      query: { page, filters: JSON.stringify(filters) },
      setResponse: (response: any) => {
        setList(response.list || []);
        setTotalPages(response.total_pages || 0);
        setLoading(false);
      },
    });
  };

  const handleDelete = (alert_id: number) => {
    setDeleteId(alert_id);
    setOpenAlert(true);
  };

  const deleteItem = async () => {
    if (!deleteId) return;
    await ApiRequest({
      url: '/safety-alerts/deleteItem',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { alert_id: deleteId },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Alerta eliminada', { variant: 'success' });
          loadList();
        } else {
          enqueueSnackbar(response.message || 'Error', { variant: 'error' });
        }
      },
    });
    setOpenAlert(false);
    setDeleteId(null);
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case 'periodic': return <Chip label="Periódica" size="small" color="primary" />;
      case 'manual': return <Chip label="Manual" size="small" color="info" />;
      case 'emergency': return <Chip label="Emergencia" size="small" color="error" />;
      default: return type;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div id="SafetyAlerts" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Alertas de Seguridad</h2>
            <span className="h2_subtitle">Monitoreo de alertas periódicas y emergencias</span>
          </div>
          <div className="boxContainerActions">
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOpenSender(true)}>
              Enviar Alerta
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Box className="box100">
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 15 }}>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filterType}
                  label="Tipo"
                  onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="periodic">Periódica</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="emergency">Emergencia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Enviada por</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Usuarios</TableCell>
                  <TableCell>Respuestas</TableCell>
                  <TableCell>Emergencias</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item: any) => (
                  <TableRow
                    key={item.alert_id}
                    sx={item.emergency_count > 0 ? { backgroundColor: '#fff3e0' } : {}}
                  >
                    <TableCell>{item.company_alert_id}</TableCell>
                    <TableCell>{typeLabel(item.alert_type)}</TableCell>
                    <TableCell>
                      {item.triggered_by_name
                        ? `${item.triggered_by_name} ${item.triggered_by_last_name || ''}`
                        : 'Sistema'}
                    </TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>{item.total_users}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${item.responded_count}/${item.total_users}`}
                        size="small"
                        color={item.responded_count === item.total_users ? 'success' : 'default'}
                        variant="outlined"
                      />
                      {item.notes_count > 0 && (
                        <ChatBubbleOutlineIcon fontSize="small" sx={{ ml: '20px', verticalAlign: 'middle', color: '#1976d2' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.emergency_count > 0 ? (
                        <Chip label={item.emergency_count} size="small" color="error" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedAlertId(item.alert_id); setOpenDetails(true); }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item.alert_id)}>
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay alertas registradas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </Box>
      </div>

      {/* Details Dialog */}
      {openDetails && selectedAlertId && (
        <SafetyAlertDetails
          alertId={selectedAlertId}
          open={openDetails}
          onClose={() => { setOpenDetails(false); setSelectedAlertId(null); }}
        />
      )}

      {/* Sender Dialog */}
      <SafetyAlertSender
        open={openSender}
        onClose={() => setOpenSender(false)}
        onSent={() => { setOpenSender(false); loadList(); }}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        send={deleteItem}
        subtitle="¿Está seguro de eliminar esta alerta?"
      />
    </div>
  );
}
